import React from "react";
import ButtonGroups, { BGItem } from "components/ButtonGroups";
import { useTranslation } from "react-i18next";
import Table from "components/Table";
import { ColumnDef } from "@tanstack/react-table";
import { EPDDetailOUT, Materials2050EPDDetailOUT } from "api-client";
import { ENVIRONMENTAL_CATEGORIES, GroupCategory, roundNumber } from "utils";

const EnvironmentalIndicators = ({
  details,
}: {
  details: Materials2050EPDDetailOUT | EPDDetailOUT | null;
}) => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = React.useState<BGItem>(
    ENVIRONMENTAL_CATEGORIES[0]
  );
  const [columnsList, setColumnsList] = React.useState<string[]>([]);
  const [dataList, setDataList] = React.useState<object[]>([]);

  React.useEffect(() => {
    if (details) {
      onChangeGroups(ENVIRONMENTAL_CATEGORIES[0]);
    }
  }, [details]);

  const onChangeGroups = (item: BGItem | undefined) => {
    if (!item) return;
    setColumnsList([
      "indicator",
      "unit",
      ...(item as GroupCategory).values.map((rec) => rec),
    ]);
    const values = details?.indicators?.map((rec) => {
      const row: { [key: string]: string | number | null | undefined } = {
        indicator: rec.indicator_name,
        unit: rec.unit,
      };
      rec.module_values.forEach((module) => {
        if (module.module_name) {
          let pretty: number | string = roundNumber(Number(module.value));

          if (pretty == 0 && !!module.value) {
            pretty = Number(module.value).toExponential(2);
          }
          row[module.module_name] = pretty;
        }
      });
      return row;
    });
    values?.length && setDataList(values?.flat());
    setSelectedCategory(item);
  };

  const getColumns = () =>
    React.useMemo<ColumnDef<{ [key: string]: string }>[]>(
      () => [
        ...columnsList.map((col) => {
          if (col === "indicator") {
            return {
              id: col,
              header: () => (
                <div className="bg-gray-50 text-gray-900 font-semibold">
                  {t("productEditor.indicator")}
                </div>
              ),
              cell: (info: {
                row: { original: { [key: string]: string } };
              }) => (
                <div className="text-gray-900 font-semibold text-xs">
                  {info.row.original[col] || "-"}
                </div>
              ),
            };
          }
          if (col === "unit") {
            return {
              id: col,
              header: () => (
                <div className="bg-gray-50 text-gray-900 font-semibold">
                  {t("EPDEditor.unit")}
                </div>
              ),
              cell: (info: {
                row: { original: { [key: string]: string } };
              }) => (
                <div className="text-gray-700 font-medium text-xs whitespace-nowrap">
                  {info.row.original.unit || "-"}
                </div>
              ),
            };
          }
          return {
            id: col,
            header: () => (
              <div className="bg-gray-50 text-gray-900 font-semibold">
                {t(`${col}`)}
              </div>
            ),
            cell: (info: { row: { original: { [key: string]: string } } }) => (
              <div>{info.row.original[col] || "-"}</div>
            ),
          };
        }),
      ],
      [columnsList]
    );

  return (
    <div className="mb-4">
      <div className="font-semibold text-base mb-3">
        {t("productEditor.environmentalIndicators")}
      </div>
      <ButtonGroups
        items={ENVIRONMENTAL_CATEGORIES}
        current={selectedCategory}
        onChange={onChangeGroups}
        optionClsssName="!h-6"
        containerClassName="mb-2"
      />
      <div className="border border-gray-300 rounded-md overflow-hidden">
        <Table
          data={dataList}
          getColumns={getColumns as () => ColumnDef<unknown, unknown>[]}
          headerRowStyle="bg-white whitespace-nowrap"
          headerColumnStyle="border-r last-of-type:border-r-0 bg-gray-50"
          headerDivStyle="!py-1"
          customColumnStyle="border-r last-of-type:border-r-0 whitespace-nowrap !py-1 !px-2 first-of-type:bg-gray-50  [&:nth-of-type(2)]:!bg-gray-50 "
          enableTooltip
        />
      </div>
    </div>
  );
};

export default EnvironmentalIndicators;
